import axios from 'axios';
import { useAuthStore } from './auth';
import { demoAdapter } from './demoInterceptor';
import type { User, Camera, CameraWithKey, CameraSettingsHistory, Organization, Branch, AnalyticsSummary, TimeseriesPoint, AuthResponse, Face, Zone, ZoneAnalyticsSummary, ZoneCountEvent, Alert, CreateZone, UpdateZone, DetectionMode, SnapshotStatus, LatestZoneCount, Product, CreateProduct, UpdateProduct, ProductSighting, MediaPlayer, MediaPlayerWithKey, Video, ZoneVideoMapping, PlaylistItem, EngagementMetrics, TrafficPatterns, CrossDemographicInsights, VideoStatsEntry, LiveFeedEvent, RegisterWithCompany, AdminCustomer, AdminDashboardStats, Subscription, RegisterDvrCamera, DvrTestInput, DvrTestResult, RegisterRtspCamera, RtspTestInput, RtspTestResult } from '../types';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests + demo mode adapter
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  // Demo mode: short-circuit all requests to in-memory mock data
  if (token === 'demo-token') {
    config.adapter = demoAdapter as any;
    return config;
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const orgId = useAuthStore.getState().organizationId;
  if (orgId) {
    config.headers['X-Organization-Id'] = orgId;
  }
  return config;
});

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        if (refreshToken) {
          const { data } = await axios.post<AuthResponse>(
          `${import.meta.env.VITE_API_BASE_URL || '/api'}/auth/refresh`,
          { refresh_token: refreshToken }
        );
          useAuthStore.getState().setAuth(data);
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          return api(originalRequest);
        }
      } catch {
        useAuthStore.getState().logout();
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (email: string, password: string) =>
    api.post<AuthResponse>('/auth/login', { email, password }).then((r) => r.data),
  register: (data: RegisterWithCompany) =>
    api.post<AuthResponse>('/auth/register', data).then((r) => r.data),
  refresh: (refreshToken: string) =>
    api.post<AuthResponse>('/auth/refresh', { refresh_token: refreshToken }).then((r) => r.data),
};

// Users
export const usersApi = {
  list: () => api.get<User[]>('/users').then((r) => r.data),
  get: (id: string) => api.get<User>(`/users/${id}`).then((r) => r.data),
  update: (id: string, data: Partial<User>) => api.put<User>(`/users/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/users/${id}`),
};

// Organizations
export const organizationsApi = {
  list: () => api.get<Organization[]>('/organizations').then((r) => r.data),
  get: (id: string) => api.get<Organization>(`/organizations/${id}`).then((r) => r.data),
  create: (name: string) => api.post<Organization>('/organizations', { name }).then((r) => r.data),
  update: (id: string, name: string) => api.put<Organization>(`/organizations/${id}`, { name }).then((r) => r.data),
  updateSettings: (id: string, settings: { face_recognition_enabled?: boolean; zone_monitoring_enabled?: boolean; timezone?: string }) =>
    api.patch<Organization>(`/organizations/${id}/settings`, settings).then((r) => r.data),
};

// Branches
export const branchesApi = {
  list: () => api.get<Branch[]>('/branches').then((r) => r.data),
  get: (id: string) => api.get<Branch>(`/branches/${id}`).then((r) => r.data),
  create: (data: { name: string; address?: string | null }) =>
    api.post<Branch>('/branches', data).then((r) => r.data),
  update: (id: string, data: { name?: string; address?: string | null }) =>
    api.put<Branch>(`/branches/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/branches/${id}`),
};

// Cameras
export interface LiveCameraStats {
  detection_count: number;
  male_count: number;
  female_count: number;
  age_distribution: Record<string, number>;
  expression_distribution: Record<string, number>;
  last_detection: string | null;
}

export const camerasApi = {
  list: () => api.get<Camera[]>('/cameras').then((r) => r.data),
  get: (id: string) => api.get<Camera>(`/cameras/${id}`).then((r) => r.data),
  register: (name: string, location?: string, branchId?: string) =>
    api.post<CameraWithKey>('/cameras', { name, location, branch_id: branchId || null }).then((r) => r.data),
  update: (id: string, data: { name?: string; location?: string; branch_id?: string | null }) =>
    api.put<Camera>(`/cameras/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/cameras/${id}`),
  move: (id: string, organizationId: string) =>
    api.post<Camera>(`/cameras/${id}/move`, { organization_id: organizationId }).then((r) => r.data),
  regenerateApiKey: (id: string) =>
    api.post<CameraWithKey>(`/cameras/${id}/regenerate-key`).then((r) => r.data),
  getLiveStats: (id: string) =>
    api.get<LiveCameraStats>(`/cameras/${id}/live-stats`).then((r) => r.data),
  updateSettings: (id: string, settings: { face_retention_minutes?: number; face_match_threshold?: number; detection_mode?: DetectionMode; detection_confidence?: number; detection_input_size?: number; stream_quality?: number; tracking_spatial_weight?: number; tracking_snap_threshold?: number }) =>
    api.patch<Camera>(`/cameras/${id}/settings`, settings).then((r) => r.data),
  getSettingsHistory: (id: string) =>
    api.get<CameraSettingsHistory[]>(`/cameras/${id}/settings-history`).then((r) => r.data),
  registerDvr: (data: RegisterDvrCamera) =>
    api.post<CameraWithKey>('/cameras/dvr', data).then((r) => r.data),
  testDvrConnection: (data: DvrTestInput) =>
    api.post<DvrTestResult>('/cameras/dvr/test', data).then((r) => r.data),
  registerRtsp: (data: RegisterRtspCamera) =>
    api.post<CameraWithKey>('/cameras/rtsp', data).then((r) => r.data),
  testRtspConnection: (data: RtspTestInput) =>
    api.post<RtspTestResult>('/cameras/rtsp/test', data).then((r) => r.data),
};

// Analytics
export const analyticsApi = {
  getSummary: (cameraIds: string[], period: string = '7d') =>
    api
      .get<AnalyticsSummary>('/analytics/summary', {
        params: { camera_ids: cameraIds.join(','), period },
      })
      .then((r) => r.data),
  getTimeseries: (cameraIds: string[], period: string = '7d', interval: number = 60) =>
    api
      .get<TimeseriesPoint[]>('/analytics/timeseries', {
        params: { camera_ids: cameraIds.join(','), period, interval },
      })
      .then((r) => r.data),
  getDemographics: (cameraIds: string[], period: string = '7d') =>
    api
      .get<{
        gender: { male: number; female: number };
        age: Record<string, number>;
        expression: Record<string, number>;
      }>('/analytics/demographics', {
        params: { camera_ids: cameraIds.join(','), period },
      })
      .then((r) => r.data),
  clearData: (cameraIds: string[]) =>
    api
      .delete<{ deleted: number }>('/analytics/clear', {
        params: { camera_ids: cameraIds.join(',') },
      })
      .then((r) => r.data),
  getEngagement: (cameraIds: string[], period: string = '7d') =>
    api
      .get<EngagementMetrics>('/analytics/engagement', {
        params: { camera_ids: cameraIds.join(','), period },
      })
      .then((r) => r.data),
  getTrafficPatterns: (cameraIds: string[], period: string = '7d') =>
    api
      .get<TrafficPatterns>('/analytics/traffic-patterns', {
        params: { camera_ids: cameraIds.join(','), period },
      })
      .then((r) => r.data),
  getCrossDemographics: (cameraIds: string[], period: string = '7d') =>
    api
      .get<CrossDemographicInsights>('/analytics/cross-demographics', {
        params: { camera_ids: cameraIds.join(','), period },
      })
      .then((r) => r.data),
  getVideoStats: (period: string = '7d') =>
    api
      .get<VideoStatsEntry[]>('/analytics/video-stats', {
        params: { period },
      })
      .then((r) => r.data),
  getLiveFeed: (cameraIds: string[], limit: number = 100) =>
    api
      .get<LiveFeedEvent[]>('/analytics/live-feed', {
        params: { camera_ids: cameraIds.join(','), limit },
      })
      .then((r) => r.data),
};

// Faces
export const facesApi = {
  list: () => api.get<Face[]>('/faces').then((r) => r.data),
  get: (id: string) => api.get<Face>(`/faces/${id}`).then((r) => r.data),
  update: (id: string, data: { label?: string | null }) =>
    api.patch<Face>(`/faces/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/faces/${id}`),
};

// Zones
export const zonesApi = {
  list: () => api.get<Zone[]>('/zones').then((r) => r.data),
  listForCamera: (cameraId: string) => api.get<Zone[]>(`/cameras/${cameraId}/zones`).then((r) => r.data),
  get: (id: string) => api.get<Zone>(`/zones/${id}`).then((r) => r.data),
  create: (cameraId: string, data: CreateZone) =>
    api.post<Zone>(`/cameras/${cameraId}/zones`, data).then((r) => r.data),
  update: (id: string, data: UpdateZone) =>
    api.put<Zone>(`/zones/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/zones/${id}`),
  getCountHistory: (zoneId: string, from?: string, to?: string) =>
    api.get<ZoneCountEvent[]>(`/zones/${zoneId}/counts`, { params: { from, to } }).then((r) => r.data),
  getAnalyticsSummary: () => api.get<ZoneAnalyticsSummary>('/analytics/zones/summary').then((r) => r.data),
  getLatestCounts: (cameraId: string) =>
    api.get<LatestZoneCount[]>(`/cameras/${cameraId}/zones/counts/latest`).then((r) => r.data),
};

// Snapshots
export const snapshotsApi = {
  get: (cameraId: string) => api.get<Blob>(`/cameras/${cameraId}/snapshot`, { responseType: 'blob', params: { t: Date.now() } }).then((r) => r.data),
  getUrl: (cameraId: string) => `${api.defaults.baseURL}/cameras/${cameraId}/snapshot`,
  getStatus: (cameraId: string) => api.get<SnapshotStatus>(`/cameras/${cameraId}/snapshot/status`).then((r) => r.data),
  request: (cameraId: string) => api.post(`/cameras/${cameraId}/request-snapshot`),
};

// Alerts
export const alertsApi = {
  list: (unreadOnly?: boolean, limit?: number) =>
    api.get<Alert[]>('/alerts', { params: { unread_only: unreadOnly, limit } }).then((r) => r.data),
  markRead: (id: number) => api.post(`/alerts/${id}/read`),
  markAllRead: () => api.post<{ marked: number }>('/alerts/mark-all-read').then((r) => r.data),
};

// Products
export const productsApi = {
  list: (params?: { category?: string; unknown_only?: boolean; active_only?: boolean }) =>
    api.get<Product[]>('/products', { params }).then((r) => r.data),
  get: (id: string) => api.get<Product>(`/products/${id}`).then((r) => r.data),
  create: (data: CreateProduct) => api.post<Product>('/products', data).then((r) => r.data),
  update: (id: string, data: UpdateProduct) => api.patch<Product>(`/products/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/products/${id}`),
  clearAll: (organizationId: string) =>
    api.delete<{ deleted: number }>('/products', { params: { organization_id: organizationId } }).then((r) => r.data),
  getZoneSightings: (zoneId: string, params?: { from?: string; to?: string; limit?: number }) =>
    api.get<ProductSighting[]>(`/zones/${zoneId}/products`, { params }).then((r) => r.data),
  updateZoneProductRecognition: (zoneId: string, enabled: boolean) =>
    api.patch(`/zones/${zoneId}/product-recognition`, { enabled }),
};

// Media Players
export const mediaPlayersApi = {
  list: () => api.get<MediaPlayer[]>('/media-players').then((r) => r.data),
  get: (id: string) => api.get<MediaPlayer>(`/media-players/${id}`).then((r) => r.data),
  create: (name: string, cameraId?: string, branchId?: string) =>
    api.post<MediaPlayerWithKey>('/media-players', { name, camera_id: cameraId || null, branch_id: branchId || null }).then((r) => r.data),
  update: (id: string, data: { name?: string; camera_id?: string | null; branch_id?: string | null }) =>
    api.put<MediaPlayer>(`/media-players/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/media-players/${id}`),
  regenerateCode: (id: string) =>
    api.post<{ registration_code: string }>(`/media-players/${id}/regenerate-code`).then((r) => r.data),
  getPlaylist: (id: string) =>
    api.get<PlaylistItem[]>(`/media-players/${id}/playlist`).then((r) => r.data),
  updatePlaylist: (id: string, items: { video_id?: string | null; item_type?: string; url?: string | null; duration_seconds?: number | null; position: number }[]) =>
    api.put<PlaylistItem[]>(`/media-players/${id}/playlist`, { items }).then((r) => r.data),
};

// Videos
export const videosApi = {
  list: () => api.get<Video[]>('/videos').then((r) => r.data),
  get: (id: string) => api.get<Video>(`/videos/${id}`).then((r) => r.data),
  upload: (file: File, onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<Video>('/videos/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    }).then((r) => r.data);
  },
  delete: (id: string) => api.delete(`/videos/${id}`),
  updateDemographics: (id: string, data: { target_gender?: string | null; target_age_min?: number | null; target_age_max?: number | null }) =>
    api.patch<Video>(`/videos/${id}/demographics`, data).then((r) => r.data),
  autoDetectDemographics: () =>
    api.post<{ updated: number; details: string[] }>('/videos/auto-detect-demographics').then((r) => r.data),
};

// Zone Video Mappings
export const zoneVideoApi = {
  getMapping: (zoneId: string) =>
    api.get<ZoneVideoMapping>(`/zones/${zoneId}/video`).then((r) => r.data),
  setMapping: (zoneId: string, videoId: string) =>
    api.put<ZoneVideoMapping>(`/zones/${zoneId}/video`, { video_id: videoId }).then((r) => r.data),
  removeMapping: (zoneId: string) => api.delete(`/zones/${zoneId}/video`),
};

// Admin (super admin)
export const adminApi = {
  listCustomers: () => api.get<AdminCustomer[]>('/admin/customers').then((r) => r.data),
  getCustomer: (id: string) => api.get<AdminCustomer>(`/admin/customers/${id}`).then((r) => r.data),
  suspendCustomer: (id: string, reason: string) =>
    api.post(`/admin/customers/${id}/suspend`, { reason }),
  unsuspendCustomer: (id: string) =>
    api.post(`/admin/customers/${id}/unsuspend`),
  updateBilling: (id: string, data: Partial<Organization>) =>
    api.put(`/admin/customers/${id}/billing`, data),
  impersonate: (id: string) =>
    api.post<{ access_token: string; user: User; organization: string; expires_in: number }>(
      `/admin/customers/${id}/impersonate`
    ).then((r) => r.data),
  getDashboardStats: () => api.get<AdminDashboardStats>('/admin/dashboard').then((r) => r.data),
  grantTrial: (organizationId: string, data: { days?: number; trial_end_date?: string }) =>
    api.post<{ message: string; subscription: Subscription }>('/subscription/trial', {
      organization_id: organizationId,
      ...data,
    }).then((r) => r.data),
};

export default api;
